<template>
  <div class="leads-dashboard">
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">My Leads</h1>
        <p class="text-gray-600 mt-1">View and manage all leads captured from your Link-in-Bio pages</p>
      </div>
      <div class="flex items-center gap-4">
        <div class="bg-green-50 px-4 py-2 rounded-lg border border-green-200">
          <span class="text-sm text-gray-600">Total Leads</span>
          <p class="text-2xl font-bold text-green-700">{{ totalLeads }}</p>
        </div>
        <a
          :href="route('leads.export')"
          class="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
        >
          <icon name="download" class="w-5 h-5" />
          Export CSV
        </a>
      </div>
    </div>

    <!-- Leads Table -->
    <div class="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bio Page</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="lead in leads.data" :key="lead.id" class="hover:bg-gray-50 transition-colors">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ lead.created_at }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {{ lead.name || 'N/A' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                <a :href="'mailto:' + lead.email" class="text-green-600 hover:text-green-800 hover:underline">
                  {{ lead.email }}
                </a>
              </td>
              <td class="px-6 py-4 text-sm text-gray-600 max-w-md">
                <div class="truncate" :title="lead.message || 'No message'">
                  {{ lead.message || '—' }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                <a
                  v-if="lead.profile"
                  :href="route('linkinbio.view', lead.profile.slug)"
                  target="_blank"
                  class="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                >
                  {{ lead.profile.name }}
                  <icon name="arrow-right" class="w-4 h-4" />
                </a>
                <span v-else class="text-gray-400">N/A</span>
              </td>
            </tr>
            <tr v-if="leads.data.length === 0">
              <td colspan="5" class="px-6 py-12 text-center">
                <div class="flex flex-col items-center gap-3">
                  <icon name="contact" class="w-16 h-16 text-gray-300" />
                  <p class="text-gray-500 text-lg font-medium">No leads yet</p>
                  <p class="text-gray-400 text-sm">Leads captured from your Link-in-Bio pages will appear here</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="leads.links && leads.links.length > 3" class="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
        <div class="text-sm text-gray-700">
          Showing {{ leads.from }} to {{ leads.to }} of {{ leads.total }} results
        </div>
        <div class="flex gap-2">
          <Link
            v-for="link in leads.links"
            :key="link.label"
            :href="link.url || '#'"
            v-html="link.label"
            :class="[
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              link.active
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300',
              !link.url ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            ]"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Link } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon.vue'
import Layout from '@/Shared/Layout'

export default {
  layout: Layout,
  components: {
    Link,
    Icon,
  },
  props: {
    leads: {
      type: Object,
      required: true,
    },
    totalLeads: {
      type: Number,
      default: 0,
    },
    title: {
      type: String,
      default: 'My Leads',
    },
  },
}
</script>

<style scoped>
.leads-dashboard {
  padding: 2rem;
}
</style>

